import {
  useV2CreatePaymentTermMutation,
  useV2UpdatePaymentTermMutation,
  useV2DeletePaymentTermMutation,
} from "@generated";
import type { V2PaymentTermResource } from "@generated";
import type { PaymentTermFormValues } from "../types";

// Build the JSON:API `data` body for create/update.
//
// - Top-level attributes are kebab-case.
// - The embedded `terms` array is sent as a FULL SET (the server destroys and
//   recreates), with snake_case member keys (`term_type`, `percentage_due`,
//   `phase_id`). percent rows omit phase_id; milestone rows omit percentage_due.
// - `position` is derived from row order (1-based).
// - The `account` relationship is inferred server-side, so it is omitted.
// - The cast satisfies string->union narrowing on `pricing-model`/`rounding`
//   (the form stores plain strings); all attributes exist in the type.
export const buildPaymentTermData = (
  values: PaymentTermFormValues,
  id?: string
): V2PaymentTermResource => {
  const terms = values.terms.map((term, index) => {
    const base: Record<string, unknown> = {
      description: term.description,
      term_type: values.termType,
      position: index + 1,
    };
    if (values.termType === "percent") {
      base.percentage_due = term.percentageDue ?? 0;
    } else if (term.phaseId != null) {
      base.phase_id = term.phaseId;
    }
    return base;
  });

  return {
    type: "payment-terms",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      "pricing-model": values.pricingModel,
      rounding: values.rounding,
      default: values.default,
      "include-expense-in-revenue": values.includeExpenseInRevenue,
      "include-expense-in-cost": values.includeExpenseInCost,
      "include-materials": values.includeMaterials,
      "travel-limit": values.travelLimit,
      terms,
    },
  } as unknown as V2PaymentTermResource;
};

export const usePaymentTermMutations = () => {
  const [create, createState] = useV2CreatePaymentTermMutation();
  const [update, updateState] = useV2UpdatePaymentTermMutation();
  const [destroy, destroyState] = useV2DeletePaymentTermMutation();

  return {
    create: (values: PaymentTermFormValues) =>
      create({ body: { data: buildPaymentTermData(values) } }).unwrap(),

    update: (id: string, values: PaymentTermFormValues) =>
      update({
        id: id,
        body: { data: buildPaymentTermData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: id }).unwrap(),

    // `active` is not in the generated resource schema; the cast lets restore
    // re-activate a soft-deleted payment term (same pattern as currencies).
    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "payment-terms",
            id: id,
            attributes: { name, active: true },
          } as unknown as V2PaymentTermResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default usePaymentTermMutations;
